import type { ReactNode } from 'react';
import { TrendChart } from '../dispatchers/TrendChart.js';
import type { AreaChartDatum } from '../themes/default/DefaultAreaChart.js';
import { LabelPill } from './LabelPill.js';

/**
 * Period axis for the revenue chart. Structurally identical to
 * `OperatorDashboardPeriod` from `@evi/shared` — declared locally
 * here so `@evi/ui` doesn't need a dependency on `@evi/shared`
 * just to share a string literal union. Consumers that already
 * import `OperatorDashboardPeriod` can pass it through unchanged
 * (TypeScript's structural typing on literal unions accepts either
 * name) — which is also why this union must stay member-for-member
 * in sync with the shared one: admin's ChartRevenue forwards the
 * shared type straight into the `period` prop.
 */
export type RevenueChartPeriod = '24h' | '7d' | '30d' | '90d';
/**
 * Label-only widening for the PWA's all-period surfaces (History, Home
 * Charging): the chart can DISPLAY a 1y / all / custom window (title
 * pill + copy) while chips and onPeriodChange stay on the narrow union
 * above — those members are never chip-emitted, so existing consumers'
 * handlers keep their exact parameter type.
 */
export type RevenueChartDisplayPeriod = RevenueChartPeriod | '1y' | 'all' | 'custom';

export interface RevenueChartProps {
  /** Time-bucketed revenue values, oldest-first. Length-flexible:
   *  24h → 24 hourly buckets; 7d → 7 daily; 30d → 30 daily. */
  data: number[];
  /** Trailing-60-min revenue rate, $/HR. Period-invariant — same
   *  value at 24h / 7d / 30d. Hidden when `showRate` is false. */
  currentRate: number;
  /** Currently selected period. Controlled — caller owns the state.
   *  Accepts the display-widened union; the extra members are label-
   *  only (no chip renders or emits them). */
  period: RevenueChartDisplayPeriod;
  /** Fires when the user taps a period chip. Caller updates `period`. */
  onPeriodChange: (next: RevenueChartPeriod) => void;
  /** RATE: CURRENT footer toggle. Default true (admin keeps it).
   *  PWA surfaces typically opt out — drivers don't read trailing
   *  rate the way operators do. Ignored if `footer` is provided. */
  showRate?: boolean;
  /** Custom footer content slot. When provided, replaces the default
   *  RATE: CURRENT footer entirely; `showRate` + `currentRate` are
   *  ignored. The footer wrapper (padding, border-top, baseline
   *  alignment) stays consistent so caller-supplied content inherits
   *  the same chrome as the default. Pass `null` to opt out without
   *  using `showRate={false}`. */
  footer?: ReactNode;
  /** Which period chips to render. Default ['24h', '7d', '30d'] —
   *  matches admin's behaviour. PWA surfaces that don't support 24h
   *  granularity (driver, owner) pass ['7d', '30d'] so the chip row
   *  honestly reflects what the consumer can serve. The `period`
   *  prop should be a member of this list; otherwise the chart still
   *  renders but the active chip is invisible. */
  allowedPeriods?: readonly RevenueChartPeriod[];
  /** Render the period chip row. Default true. A page whose own period
   *  picker already drives this chart passes false, so the reader sees
   *  one picker instead of two. The LAST 7D pill on the right still
   *  names the active period. */
  showPeriodChips?: boolean;
  /** Custom title-pill label. Default "REVENUE: USD" — Reports'
   *  Energy tab passes "ENERGY: kWh" so the same chart renders
   *  either dimension without a forked component. Backward
   *  compatible: existing callers don't pass it and keep the
   *  revenue label. */
  titleLabel?: string;
  /** Accessible name of the period chip group. Default "Revenue period";
   *  the admin's Site roles pass "Charging sales period" to match their
   *  CHARGING SALES title. */
  periodAriaLabel?: string;
  /** What the all-zero empty state says there is none of: "No {emptyNoun}
   *  in the last 24 hours." Default "revenue"; the admin's Site roles pass
   *  "charging sales". */
  emptyNoun?: string;
  /** Custom Y-axis + last-point label formatter. Default
   *  `$${round(n)}` for revenue dollars. Energy callers pass a
   *  formatter that renders kWh; any caller can swap to a
   *  domain-specific format. */
  formatY?: (n: number) => string;
}

// Record + ordered-keys split: Record gives exhaustive non-null
// access by typing (`PERIOD_LOOKUP[period]` always resolves), the
// array preserves render order for the chip row.
const PERIOD_LOOKUP: Record<RevenueChartDisplayPeriod, { label: string; pill: string; copy: string }> = {
  '24h': { label: '24H', pill: 'LAST 24H', copy: 'last 24 hours' },
  '7d':  { label: '7D',  pill: 'LAST 7D',  copy: 'last 7 days' },
  '30d': { label: '30D', pill: 'LAST 30D', copy: 'last 30 days' },
  '90d': { label: '90D', pill: 'LAST 90D', copy: 'last 90 days' },
  '1y':  { label: '1Y',  pill: 'LAST YEAR', copy: 'last year' },
  'all': { label: 'ALL', pill: 'ALL TIME', copy: 'all time' },
  custom: { label: 'CUSTOM', pill: 'CUSTOM RANGE', copy: 'custom range' },
};
// '90d' and later are deliberately NOT in the default order — the
// dashboard's chip row stays 24h/7d/30d. Surfaces that serve wider
// windows opt in via `allowedPeriods`, filtered against the FULL
// order below so opted-in chips actually render (filtering against
// the default order silently dropped 90d before).
const PERIOD_ORDER: ReadonlyArray<RevenueChartPeriod> = ['24h', '7d', '30d'];
const PERIOD_ORDER_FULL: ReadonlyArray<RevenueChartPeriod> = ['24h', '7d', '30d', '90d'];

/**
 * Period-toggled revenue chart. PeriodChip row + LabelPill title +
 * adaptive period pill + TrendChart + optional RATE: CURRENT
 * footer. Originally inlined as `RevenueCard` in the admin's
 * `OperatorDashboard.tsx`; promoted to `@evi/ui` so PWA surfaces
 * (HistoryPage, Earnings page) can consume the same visual.
 *
 * Chrome: the component itself is chrome-less. It assumes its
 * parent provides bg / border / radius / shadow — admin uses the
 * dashboard grid's `.widget` class (var(--evi-widget-*) tokens);
 * PWA surfaces should wrap in a `.card`-equivalent surface or
 * inherit the section's chrome. The wrapper is a transparent
 * `height: 100%` flex column so it stretches to fill the parent.
 *
 * Tokens consumed (all from `@evi/skins`):
 *   --evi-surface-2 (period chip rail bg)
 *   --evi-border    (rail border, footer separator)
 *   --evi-accent    (active chip bg)
 *   --evi-text-mute (inactive chip text)
 *   --evi-power     (rate value)
 *   --evi-mute      (rate unit)
 */
export function RevenueChart({
  data,
  currentRate,
  period,
  onPeriodChange,
  showRate = true,
  footer,
  allowedPeriods,
  showPeriodChips = true,
  titleLabel = 'REVENUE: USD',
  periodAriaLabel = 'Revenue period',
  emptyNoun = 'revenue',
  formatY,
}: RevenueChartProps) {
  // Footer slot priority:
  //   1. `footer` prop provided (including null) → render that
  //   2. `showRate === false` → no footer
  //   3. default → RATE: CURRENT footer with `currentRate`
  const hasCustomFooter = footer !== undefined;
  const renderDefaultFooter = !hasCustomFooter && showRate !== false;
  // Chip row honours the full default order (24h/7d/30d) unless the
  // consumer narrows via `allowedPeriods`. Filter PERIOD_ORDER so the
  // chip rail still preserves canonical left-to-right ordering.
  const visiblePeriods: readonly RevenueChartPeriod[] = allowedPeriods
    ? PERIOD_ORDER_FULL.filter((p) => allowedPeriods.includes(p))
    : PERIOD_ORDER;
  // Synthesize x-axis labels. The label shape depends on bucket
  // cadence: 24h → "-23h" / "-12h" / "NOW"; 7d / 30d → "-6d" /
  // "-1d" / "TODAY". Luce's sparkline ignores x values entirely;
  // labels are for the default theme's chart axes.
  const isHourly = period === '24h';
  const trend: AreaChartDatum[] = data.map((y, i) => {
    const stepsAgo = data.length - 1 - i;
    let label: string;
    if (isHourly) {
      label = stepsAgo === 0 ? 'NOW' : `-${stepsAgo}h`;
    } else {
      label = stepsAgo === 0 ? 'TODAY' : `-${stepsAgo}d`;
    }
    return { x: label, y };
  });

  // Empty-state detection: if every revenue bucket is zero, render
  // an explanatory message instead of the flat-zero line. Keeps the
  // operator (or driver) from thinking the chart is broken when the
  // truth is that no qualifying sessions ended in the chosen window.
  const allZero = data.length === 0 || data.every((v) => v === 0);
  const active = PERIOD_LOOKUP[period];

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <LabelPill tone="power" size="md">{titleLabel}</LabelPill>
        {/* Period chip row — chips for 24H / 7D / 30D. Visuals mirror
            the PWA HistoryPage's PeriodChip pattern using the same
            EVI tokens so the chart reads as one product across
            admin + driver surfaces. */}
        {showPeriodChips && (
        <div
          role="tablist"
          aria-label={periodAriaLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: 'var(--evi-surface-2)',
            border: '1px solid var(--evi-border)',
            borderRadius: '999px',
            padding: '2px',
          }}
        >
          {visiblePeriods.map((value) => {
            const isActive = value === period;
            const opt = PERIOD_LOOKUP[value];
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onPeriodChange(value)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: isActive ? 'var(--evi-accent)' : 'transparent',
                  color: isActive ? '#001a08' : 'var(--evi-text-mute)',
                  font: 'inherit',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  padding: '5px 10px',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease, color 0.15s ease',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        )}
        <LabelPill tone="mute">{active.pill}</LabelPill>
      </div>

      {allZero ? (
        // Empty state fills whatever vertical room is left in the
        // parent surface (header + footer are intrinsic; this
        // placeholder takes the rest via flex:1). Earlier code
        // pinned this at height:220 to match TrendChart's default,
        // but that overflowed shrunken admin grid cells. The
        // populated TrendChart branch keeps its own intrinsic
        // height; only the empty state adapts.
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--evi-text-mute)',
            fontSize: '13px',
            textAlign: 'center',
            padding: '0 12px',
          }}
        >
          {`No ${emptyNoun} in the `}{active.copy}.
        </div>
      ) : (
        <TrendChart
          data={trend}
          formatY={formatY ?? ((n) => `$${Math.round(n)}`)}
        />
      )}

      {(hasCustomFooter || renderDefaultFooter) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginTop: '8px',
            paddingTop: '10px',
            borderTop: '1px solid var(--evi-border)',
          }}
        >
          {hasCustomFooter ? (
            // Caller-supplied slot. The wrapper above keeps padding /
            // border-top / baseline alignment consistent with the
            // default footer so per-surface content (PWA period
            // aggregate, etc.) reads with the same chrome.
            footer
          ) : (
            <>
              {/* "Rate: current" is period-INVARIANT. It's a moment-
                  in-time pricing read (trailing 60 min revenue), not
                  a window aggregate. Stays the same whether period
                  is 24h, 7d, or 30d. */}
              <LabelPill tone="power">RATE: CURRENT</LabelPill>
              <span>
                <span
                  style={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: 'var(--evi-power)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {Math.round(currentRate)}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: 'var(--evi-mute)',
                    marginLeft: '2px',
                    letterSpacing: '1px',
                  }}
                >
                  $/HR
                </span>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
